import React, { useState } from 'react';
import { CheckCircle2, ArrowUpRight } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { SEO } from '../components/SEO';
import api from '../lib/api';
import { B2B } from '../constants/testIds';
import { IMG } from '../lib/images';

export default function B2BPage() {
  const { t } = useI18n();
  const fields = t('b2b.fields');
  const types = t('b2b.types');
  const why = t('b2b.why');

  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    tahiti_number: '',
    phone: '',
    email: '',
    address: '',
    business_type: 'distributor',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/b2b', form);
      setSuccess(true);
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Une erreur est survenue.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-background">
      <SEO
        title={t('b2b.title')}
        description="Devenez distributeur ou revendeur de Tahitian Move — marque polynésienne depuis 1977. Demande de catalogue professionnel."
      />

      {/* Hero */}
      <section className="relative pt-40 pb-24 md:pt-48 md:pb-32 bg-ocean text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={IMG.lagoon} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="eyebrow text-lagoon">{t('b2b.subtitle')}</p>
          <h1 className="display text-6xl md:text-8xl lg:text-9xl mt-6 max-w-4xl">
            {t('b2b.title')}
          </h1>
          <p className="mt-10 text-white/75 max-w-2xl text-base md:text-lg leading-relaxed">
            {t('b2b.intro')}
          </p>
        </div>
      </section>

      {/* Why */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="eyebrow">{t('b2b.whyTitle')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 mt-12">
            {why.map((w, i) => (
              <div key={i} className="border-t border-sand pt-8">
                <p className="font-serif text-5xl text-lagoon">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-serif text-2xl md:text-3xl text-ink mt-6">{w.title}</h3>
                <p className="text-ink-soft mt-4 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form — Distributor / Retailer catalog request */}
      <section className="bg-paper py-24 md:py-32 border-t border-sand">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <p className="eyebrow">Espace Pro</p>
          <h2 className="display text-4xl md:text-5xl mt-4 text-ink leading-tight">
            {t('b2b.formTitle')}.
          </h2>
          <p className="text-ink-soft mt-6 max-w-2xl">{t('b2b.formIntro')}</p>

          {success ? (
            <div
              data-testid={B2B.success}
              className="mt-12 border border-lagoon/40 bg-lagoon/5 p-8 md:p-12 flex items-start gap-4"
            >
              <CheckCircle2 className="text-lagoon shrink-0 mt-1" />
              <div>
                <p className="font-serif text-2xl text-ink">{t('b2b.success')}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label={fields.type} required>
                <select
                  data-testid={B2B.type}
                  required
                  value={form.business_type}
                  onChange={(e) => update('business_type', e.target.value)}
                  className="tm-input"
                >
                  <option value="distributor">{types.distributor}</option>
                  <option value="retailer">{types.retailer}</option>
                </select>
              </Field>
              <Field label={fields.company} required>
                <input
                  data-testid={B2B.company}
                  required
                  value={form.company_name}
                  onChange={(e) => update('company_name', e.target.value)}
                  className="tm-input"
                />
              </Field>
              <Field label={fields.contactName} required>
                <input
                  data-testid={B2B.contactName}
                  required
                  value={form.contact_name}
                  onChange={(e) => update('contact_name', e.target.value)}
                  className="tm-input"
                />
              </Field>
              <Field label={fields.tahitiNumber} required>
                <input
                  data-testid="b2b-tahiti-number"
                  required
                  value={form.tahiti_number}
                  onChange={(e) => update('tahiti_number', e.target.value)}
                  className="tm-input"
                  placeholder="N° TAHITI"
                />
              </Field>
              <Field label={fields.phone} required>
                <input
                  data-testid={B2B.phone}
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className="tm-input"
                />
              </Field>
              <Field label={fields.email} required>
                <input
                  data-testid={B2B.email}
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  className="tm-input"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label={fields.address} required>
                  <input
                    data-testid="b2b-address"
                    required
                    value={form.address}
                    onChange={(e) => update('address', e.target.value)}
                    className="tm-input"
                    placeholder="Rue, ville, code postal, pays"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label={fields.message}>
                  <textarea
                    data-testid={B2B.message}
                    rows={4}
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    className="tm-input resize-none"
                  />
                </Field>
              </div>
              {error && <p className="md:col-span-2 text-red-600 text-sm">{error}</p>}
              <p className="md:col-span-2 text-xs text-ink-muted mt-2">
                Les champs marqués <span className="text-lagoon">*</span> sont obligatoires.
              </p>
              <div className="md:col-span-2 mt-4 flex justify-end">
                <button
                  data-testid={B2B.submit}
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-50"
                >
                  {submitting ? t('common.sending') : 'Recevoir le catalogue'}
                  <ArrowUpRight size={14} />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="tm-label">{label}{required && <span className="text-lagoon"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
